import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Button from '@pelckmans/elementis/components/button';
import Page from '@pelckmans/elementis/components/page';
import SearchField from '@pelckmans/elementis/components/search-field';
import Section from '@pelckmans/elementis/components/section';
import SidebarIcon from '@pelckmans/elementis/icons/sidebar';
import keyBy from 'lodash/keyBy';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { fetchTocNodes, NodeWithoutDescription } from '../../../../api/table-of-content';
import useDocumentTitle from '../../../../hooks/useDocumentTitle';
import { useRoutes } from '../../../../hooks/useRoutes';
import ContentBody from '../../../../layout/content/ContentBody';
import ContentHeader from '../../../../layout/content/ContentHeader';
import AggregatedModuleAlerts from '../../../../shared-components/alerts/AggregatedModuleAlerts';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../../../shared-components/elementis/hoc/withScrollThreshold';
import EmptyState from '../../../../shared-components/EmptyState';
import LoadingState from '../../../../shared-components/LoadingState';
import ModuleContext from '../../_contexts/ModuleContext';
import SidebarVisibilityContext from '../../_contexts/SidebarVisibilityContext';
import SuperModuleBreadcrumb from '../../components/breadcrumbs/SuperModuleBreadcrumb';
import { CategoricalContentTypeContext } from '../context/CategoricalContentTypeContext';
import { QueryMaterialContext, SEARCH_PARAMS } from '../context/QueryMaterialContext';
import { flattenCategoricalMaterialPerModule } from '../utils/categoricalMaterialUtils';
import { groupResultsByModule } from '../utils/groupResultsByModule';
import ResultsForModule from './ResultsForModule';

const BASE = 'PAGES.CATEGORICAL.BROWSE_MODE';

function CategoricalBrowseMode({ pastThreshold }: WithScrollThresholdProps) {
  const { t } = useTranslation();
  const { module, allModules } = useContext(ModuleContext);
  const { materialCategory } = useParams();
  const { categoricalContentPerModule } = useContext(CategoricalContentTypeContext);
  const {
    modulesForResults,
    materialResults,
    setSearchParam,
    loading: loadingMaterial,
    refetch,
  } = useContext(QueryMaterialContext);
  const [groupedResults, setGroupedResults] = useState<ReturnType<typeof groupResultsByModule>>([]);
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(searchParams.get(SEARCH_PARAMS.SEARCH) || '');
  const [tocByModule, setTocByModule] = useState<Record<string, NodeWithoutDescription[]>>({});
  const { buildAbsolutePath } = useRoutes();

  const modulesById = keyBy(allModules, 'id');
  const categoricalContent = flattenCategoricalMaterialPerModule(categoricalContentPerModule);
  const category = categoricalContent.find(category => category.name === materialCategory);

  const {
    medialinks: { data: medialinks, total: medialinksTotal },
    userMaterial: { data: userMaterials, total: userMaterialsTotal },
    slideSets: { data: slideSets, total: slideSetsTotal },
    sharedMaterial: { data: sharedMaterials, total: sharedMaterialsTotal },
  } = materialResults;

  const totalResults = medialinksTotal + userMaterialsTotal + slideSetsTotal + sharedMaterialsTotal;

  const params = {
    subjectSlug: module?.subjectSlug || '',
    methodSlug: module?.methodSlug || '',
    moduleSlug: module?.slug || '',
  };

  useDocumentTitle(t(`PAGES.CATEGORICAL.DOCUMENT_TITLE`));

  useEffect(() => {
    async function fetchTocByModule() {
      const tocs = await Promise.all(allModules.map(m => fetchTocNodes(m.id).catch(() => undefined)));
      const tocsByModule = allModules.reduce<Record<string, NodeWithoutDescription[]>>((acc, m, idx) => {
        if (!tocs[idx]) return acc;
        const nodes = tocs[idx].data;

        acc[m.id] = nodes;

        return acc;
      }, {});
      setTocByModule(tocsByModule);
    }

    fetchTocByModule();
  }, [allModules]);

  useEffect(() => {
    const getGroupedResults = async () => {
      if (
        medialinks.length === 0 &&
        userMaterials.length === 0 &&
        slideSets.length === 0 &&
        sharedMaterials.length === 0
      ) {
        setGroupedResults([]);
        return;
      }

      const results = groupResultsByModule(
        modulesForResults,
        medialinks,
        userMaterials,
        slideSets,
        sharedMaterials,
        tocByModule,
      );
      setGroupedResults(results);
    };

    getGroupedResults();
  }, [medialinks, modulesForResults, sharedMaterials, slideSets, userMaterials, tocByModule]);

  useEffect(() => {
    setSearchValue(searchParams.get(SEARCH_PARAMS.SEARCH) || '');
  }, [searchParams]);

  const selectedModules = groupedResults.map(result => modulesById[result.moduleId]);

  const handleClear = () => {
    setSearchValue('');
    setSearchParam({ [SEARCH_PARAMS.SEARCH]: '' });
  };

  const { setSidebarVisibility } = useContext(SidebarVisibilityContext);

  function renderContentBody() {
    if (loadingMaterial) {
      return (
        <Section title={t(`${BASE}.EMPTY_STATE.SECTION_TITLE`)}>
          <LoadingState variant="list" aria-label={t(`${BASE}.LOADING_STATE.A11Y_LABEL`)} />
        </Section>
      );
    }

    if (totalResults === 0 || groupedResults.length === 0) {
      return (
        <Section title={t(`${BASE}.EMPTY_STATE.SECTION_TITLE`)}>
          <EmptyState
            variant="list"
            headerTitle={t(`${BASE}.EMPTY_STATE.TITLE`)}
            bodyText={t(`${BASE}.EMPTY_STATE.BODY`)}
          />
        </Section>
      );
    }

    return (
      <div className="max-container material-sections">
        {groupedResults.map(resultsForModule => (
          <ResultsForModule
            key={resultsForModule.moduleId}
            resultsForModule={resultsForModule}
            module={modulesById[resultsForModule.moduleId]}
            refetch={refetch}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      <ContentHeader
        cover={
          <Page.Header
            density={pastThreshold ? 'compact' : 'normal'}
            headerText={category?.name || ''}
            breadcrumbs={
              <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
                <SuperModuleBreadcrumb />
                <Breadcrumbs.Item to={buildAbsolutePath(['MATERIAL', 'MATERIAL.CATEGORICAL'], params)}>
                  {t('BREADCRUMBS.CATEGORICAL_MATERIAL_LEVEL_PAGE')}
                </Breadcrumbs.Item>
              </Breadcrumbs>
            }
            extra={
              <SearchField
                value={searchValue}
                placeholder={t(`${BASE}.HEADER.SEARCH_FIELD.PLACEHOLDER`)}
                searchActionLabel={t(`${BASE}.HEADER.SEARCH_FIELD.BUTTON_TEXT`)}
                clearActionLabel={t(`${BASE}.HEADER.SEARCH_FIELD.CLEAR_ACTION_LABEL`)}
                onChange={e => setSearchValue(e.target.value)}
                onClear={handleClear}
                onSearch={() => setSearchParam({ [SEARCH_PARAMS.SEARCH]: searchValue })}
              />
            }
            contextButton={
              <Button startIcon={<SidebarIcon />} onClick={() => setSidebarVisibility(true)}>
                {t(`${BASE}.HEADER.CONTEXT_BUTTON.OPEN_SIDEBAR`)}
              </Button>
            }
          />
        }
      />
      <ContentBody alerts={<AggregatedModuleAlerts selectedModules={selectedModules} />}>
        {renderContentBody()}
      </ContentBody>
    </>
  );
}

export default withScrollThreshold(CategoricalBrowseMode);
